
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const DiscountCalculator = () => {
  // Calculate discounted price
  const [originalPrice, setOriginalPrice] = useState<string>('100');
  const [discountPercent, setDiscountPercent] = useState<string>('20');
  const [discountedPrice, setDiscountedPrice] = useState<number | null>(null);
  const [savings, setSavings] = useState<number | null>(null);

  // Calculate discount percentage
  const [originalPrice2, setOriginalPrice2] = useState<string>('100');
  const [salePrice, setSalePrice] = useState<string>('80');
  const [calculatedDiscount, setCalculatedDiscount] = useState<number | null>(null);

  const [error, setError] = useState<string | null>(null);

  const calculateDiscountedPrice = () => {
    try {
      setError(null);
      
      const price = parseFloat(originalPrice);
      const discount = parseFloat(discountPercent);

      if (isNaN(price) || isNaN(discount)) {
        setError("Please enter valid numbers");
        return;
      }

      if (price < 0 || discount < 0 || discount > 100) {
        setError("Price must be positive and discount must be between 0-100%");
        return;
      }

      const discountAmount = (price * discount) / 100;
      const finalPrice = price - discountAmount;

      setDiscountedPrice(finalPrice);
      setSavings(discountAmount);

    } catch (err) {
      setError("Error calculating discount");
    }
  };

  const calculateDiscountPercent = () => {
    try {
      setError(null);
      
      const original = parseFloat(originalPrice2);
      const sale = parseFloat(salePrice);

      if (isNaN(original) || isNaN(sale)) {
        setError("Please enter valid numbers");
        return;
      }

      if (original <= 0 || sale < 0) {
        setError("Prices must be positive");
        return;
      }

      if (sale > original) {
        setError("Sale price cannot be higher than original price");
        return;
      }

      const discountPercentage = ((original - sale) / original) * 100;
      setCalculatedDiscount(discountPercentage);

    } catch (err) {
      setError("Error calculating discount percentage");
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Tabs defaultValue="calculate-price">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="calculate-price">Calculate Price</TabsTrigger>
          <TabsTrigger value="calculate-percent">Calculate Discount %</TabsTrigger>
        </TabsList>

        <TabsContent value="calculate-price">
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-4">Discount Price Calculator</h2>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="original-price">Original Price ($)</Label>
                  <Input
                    id="original-price"
                    type="number"
                    value={originalPrice}
                    onChange={(e) => setOriginalPrice(e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="discount-percent">Discount Percentage (%)</Label>
                  <Input
                    id="discount-percent"
                    type="number"
                    max="100"
                    value={discountPercent}
                    onChange={(e) => setDiscountPercent(e.target.value)}
                    className="mt-1"
                  />
                </div>

                <Button onClick={calculateDiscountedPrice} className="w-full">
                  Calculate Discounted Price
                </Button>

                {error && (
                  <div className="p-3 bg-red-100 text-red-700 rounded-md text-sm">
                    {error}
                  </div>
                )}

                {discountedPrice !== null && savings !== null && (
                  <div className="space-y-3">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h3 className="font-medium">Final Price</h3>
                      <p className="text-2xl font-bold">${discountedPrice.toFixed(2)}</p>
                    </div>
                    
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h3 className="font-medium">You Save</h3>
                      <p className="text-xl font-bold">${savings.toFixed(2)}</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="calculate-percent">
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-4">Discount Percentage Calculator</h2>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="original-price-2">Original Price ($)</Label>
                  <Input
                    id="original-price-2"
                    type="number"
                    value={originalPrice2}
                    onChange={(e) => setOriginalPrice2(e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="sale-price">Sale Price ($)</Label>
                  <Input
                    id="sale-price"
                    type="number"
                    value={salePrice}
                    onChange={(e) => setSalePrice(e.target.value)}
                    className="mt-1"
                  />
                </div>

                <Button onClick={calculateDiscountPercent} className="w-full">
                  Calculate Discount Percentage
                </Button>

                {error && (
                  <div className="p-3 bg-red-100 text-red-700 rounded-md text-sm">
                    {error}
                  </div>
                )}

                {calculatedDiscount !== null && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-medium">Discount Percentage</h3>
                    <p className="text-2xl font-bold">{calculatedDiscount.toFixed(1)}%</p>
                    <p className="text-sm text-gray-600 mt-2">
                      You save ${(parseFloat(originalPrice2) - parseFloat(salePrice)).toFixed(2)}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DiscountCalculator;
